import { NgClass, NgIf } from '@angular/common';
import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	OnDestroy,
	OnInit,
	ViewChild,
	ViewEncapsulation
} from '@angular/core';
import { MatBadge } from '@angular/material/badge';
import { MatIconButton } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuTrigger } from '@angular/material/menu';
import { MatTooltip } from '@angular/material/tooltip';
import { TranslocoPipe } from '@jsverse/transloco';
import { Actions, ofType } from '@ngrx/effects';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { listItemTooltipShowDelay } from 'app/common/app-constants';
import { NotificationGroups } from 'app/common/notification.constants';
import { isNullOrUndefined } from 'app/common/utils/utils.object';
import { GeneratedFileButtonComponent } from 'app/layout/common/generated-files/generated-file-button/generated-file-button.component';
import { GeneratedFileModel, GeneratedFileTypeModel } from '@twrx-api-models';
import { EnumTranslatePipe } from 'app/shared/pipes/enum-translate.pipe';
import {
	deleteGeneratedFile,
	downloadGeneratedFile,
	loadCurrentUserGeneratedFile,
	subscribeToGroups,
	unSubscribeFromGroups
} from 'app/store/account/account.actions';
import { selectGeneratedFile } from 'app/store/account/account.selectors';
import { IAccountState } from 'app/store/account/account.state';
import { BaseApiComponent } from 'app/common/base/base-api-component';
import { NotificationSubscriptionModel } from 'app/models/dto/notification-subscription';
import { buildGroups } from 'app/common/utils/utils.notification.groups';
import { filter } from 'rxjs';
import { FacilityActions } from 'app/store/facility/facility.actions';
import { CustomerActions } from 'app/store/customer/customer.actions';
import { FormularyNdcActions } from 'app/store/formulary-ndc/formulary-ndc.actions';
import { RebateAggregatorNdcRatesActions } from 'app/store/rebate-aggregator-ndc-rates/rebate-aggregator-ndc-rates.actions';
import { RebateGroupAggregatorNdcActions } from 'app/store/rebate-group-aggregator-ndc/rebate-group-aggregator-ndc.actions';
import { NdcDestinationHistoryActions } from 'app/store/ndc-destination-history/ndc-destination-history.actions';

@UntilDestroy()
@Component({
	selector:        'app-generated-files',
	templateUrl:     './generated-files.component.html',
	encapsulation:   ViewEncapsulation.None,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports:         [
		MatDivider,
		MatIcon,
		MatIconButton,
		MatMenu,
		TranslocoPipe,
		MatMenuTrigger,
		MatTooltip,
		EnumTranslatePipe,
		GeneratedFileButtonComponent,
		NgIf,
		NgClass,
		MatBadge
	]
})
export class GeneratedFilesComponent
	extends BaseApiComponent
	implements OnInit, OnDestroy
{
	@ViewChild('userFilesMenuTrigger') userFilesMenuTrigger: MatMenuTrigger;

	public readonly GeneratedFileTypeModel = GeneratedFileTypeModel;
	public readonly listItemTooltipShowDelay: number = listItemTooltipShowDelay;

	public file:       GeneratedFileModel;
	public fileExists: boolean = false;
	public groups:     NotificationSubscriptionModel[];

	private isUserTriggeredAction: boolean = false;

	constructor(
		private readonly store: Store<IAccountState>,
		private readonly changeDetectorRef: ChangeDetectorRef,
		private readonly actions: Actions
	) {
		super();
	}

	ngOnInit(): void {
		this.actions
			.pipe(
				ofType(
					FacilityActions.LocationsFileGenerationStarted,
					CustomerActions.CustomerContractPaymentFileGenerationStarted,
					CustomerActions.CustomerLocationsFileGenerationStarted,
					CustomerActions.CustomersFileGenerationStarted,
					FormularyNdcActions.FormularyNdcListFileGenerationStarted,
					NdcDestinationHistoryActions.StartNdcDestinationHistoryDetailsFileGeneration,
					RebateAggregatorNdcRatesActions.RebateAggregatorNdcRatesFileGenerationStarted,
					RebateGroupAggregatorNdcActions.RebateGroupAggregatorNdcFileGenerationStarted,
					RebateGroupAggregatorNdcActions.RebateGroupAggregatorNdcFilteredFileGenerationStarted
				),
				untilDestroyed(this)
			)
			.subscribe(() => {
				this.isUserTriggeredAction = true;
			});

		this.loadUserGeneratedFiles();

		this.groups = buildGroups([NotificationGroups.GeneratedFile]);

		this.store.dispatch(
			subscribeToGroups(this.createRequestPayload(this.groups))
		);

		this.store
			.select(selectGeneratedFile)
			.pipe(
				untilDestroyed(this),
				filter(
					(x: GeneratedFileModel) =>
						isNullOrUndefined(x)
						|| !this.fileExists
						|| this.file.id !== x.id
						|| this.file.status !== x.status
				)
			)
			.subscribe((x: GeneratedFileModel) => {
				if (
					!isNullOrUndefined(x)
					&& isNullOrUndefined(x.id)
					&& !isNullOrUndefined(x.status)
				) {
					this.loadUserGeneratedFiles();

					return;
				}

				this.file = x;
				this.fileExists = !isNullOrUndefined(x);

				const menuOpened: boolean = this.userFilesMenuTrigger?.menuOpen;

				if (this.fileExists && this.isUserTriggeredAction) {
					if (!menuOpened) {
						this.userFilesMenuTrigger?.openMenu();
					}

					this.isUserTriggeredAction = false;
				} else if (!this.fileExists && menuOpened) {
					this.userFilesMenuTrigger?.closeMenu();
				}

				this.changeDetectorRef.markForCheck();
			});
	}

	ngOnDestroy(): void {
		this.store.dispatch(
			unSubscribeFromGroups(this.createRequestPayload(this.groups))
		);

		super.ngOnDestroy();
	}

	public download(): void {
		this.store.dispatch(
			downloadGeneratedFile(this.createRequestPayload(this.file.id))
		);
	}

	public delete(): void {
		this.store.dispatch(deleteGeneratedFile(this.createRequestPayload()));
	}

	public cancel(): void {
		this.userFilesMenuTrigger.closeMenu();
	}

	private loadUserGeneratedFiles(): void {
		this.store.dispatch(
			loadCurrentUserGeneratedFile(this.createRequestPayload())
		);
	}
}
